import styled from "styled-components";
import { Container } from "./Container";
import { useCallback, useMemo, useState } from "react";
import { GTM_EMAIL_EVENT, SEND_EMAIL } from "../modules/constants";
import { gtagReportConversion, replaceCarriageReturns } from "../modules/utils";

const Section = styled.section`
  padding: var(--section-y) 0;
`;

const Header = styled.div`
  display: grid;
  gap: 12px;
  margin-bottom: 22px;
`;

const Title = styled.h2`
  color: var(--text-0);
  font-size: clamp(28px, 3.6vw, 44px);
  letter-spacing: -0.03em;
  line-height: 1.08;
`;

const Lead = styled.p`
  max-width: 80ch;
`;

const Panel = styled.div`
  border: 1px solid var(--border-0);
  background: var(--surface-0);
  border-radius: var(--radius-lg);
  padding: 18px;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.18) inset;
`;

const Form = styled.form`
  display: grid;
  gap: 16px;
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;

  @media screen and (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  display: grid;
  gap: 8px;
`;

const Label = styled.label`
  text-transform: uppercase;
  letter-spacing: 0.14em;
  font-weight: 800;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.72);
`;

const Input = styled.input`
  width: 100%;
  padding: 14px 14px;
  border-radius: var(--radius-md);
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: var(--surface-0);
  color: var(--text-0);

  outline: none;
  transition: border-color 160ms ease, background 160ms ease,
    box-shadow 160ms ease;

  &::placeholder {
    color: rgba(255, 255, 255, 0.45);
  }

  &:focus {
    border-color: rgba(185, 255, 102, 0.55);
    background: var(--surface-1);
    box-shadow: 0 0 0 4px rgba(185, 255, 102, 0.14);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 14px 14px;
  min-height: 180px;
  resize: vertical;

  border-radius: var(--radius-md);
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: var(--surface-0);
  color: var(--text-0);

  outline: none;
  transition: border-color 160ms ease, background 160ms ease,
    box-shadow 160ms ease;

  &::placeholder {
    color: rgba(255, 255, 255, 0.45);
  }

  &:focus {
    border-color: rgba(185, 255, 102, 0.55);
    background: var(--surface-1);
    box-shadow: 0 0 0 4px rgba(185, 255, 102, 0.14);
  }
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const SubmitButton = styled.button`
  appearance: none;
  border: 1px solid rgba(185, 255, 102, 0.45);
  background: linear-gradient(
    135deg,
    rgba(185, 255, 102, 0.18),
    rgba(124, 92, 255, 0.14)
  );
  color: var(--text-0);
  padding: 12px 18px;
  border-radius: 999px;
  cursor: pointer;
  font-weight: 850;
  letter-spacing: 0.02em;
  min-width: 120px;

  transition: transform 160ms ease, background 160ms ease,
    border-color 160ms ease, opacity 160ms ease;

  &:hover {
    transform: translateY(-1px);
    border-color: rgba(185, 255, 102, 0.65);
    background: linear-gradient(
      135deg,
      rgba(185, 255, 102, 0.22),
      rgba(124, 92, 255, 0.18)
    );
  }

  &:active {
    transform: translateY(0px);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const initialFormState = {
  firstName: "",
  lastName: "",
  phoneNumber: "",
  email: "",
  message: "",
};

export const Contact = () => {
  const [formValues, setFormValues] = useState(initialFormState);

  const { firstName, lastName, phoneNumber, email, message } = formValues;

  const onFormChange = useCallback(
    (e) => {
      setFormValues({ ...formValues, [e.target.name]: e.target.value });
    },
    [formValues, setFormValues]
  );

  const isValid = useMemo(() => {
    return [firstName, lastName, phoneNumber, email, message].every(
      (value) => value.trim().length > 0
    );
  }, [firstName, lastName, phoneNumber, email, message]);

  const sendEmail = useCallback(() => {
    if (!isValid) return;

    const body = replaceCarriageReturns(
      `${message}%0D%0A%0D%0APhone: ${phoneNumber}%0D%0AEmail: ${email}%0D%0AName: ${firstName} ${lastName}`
    );
    const href = `mailto:${SEND_EMAIL}?subject=Auxiliary One Site Inquiry&body=${body}`;

    window.open(href);
    setFormValues(initialFormState);

    gtagReportConversion(undefined, GTM_EMAIL_EVENT);
  }, [email, firstName, lastName, phoneNumber, message, isValid]);

  return (
    <Section id="contact">
      <Container>
        <Header>
          <Title>Contact</Title>
          <Lead>Think we can help with a Project? Send us a Message!</Lead>
        </Header>

        <Panel>
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              sendEmail();
            }}
          >
            <FieldGrid>
              <Field>
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  name="firstName"
                  value={firstName}
                  onChange={onFormChange}
                  placeholder="First name"
                  autoComplete="given-name"
                  required
                />
              </Field>
              <Field>
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  name="lastName"
                  value={lastName}
                  onChange={onFormChange}
                  placeholder="Last name"
                  autoComplete="family-name"
                  required
                />
              </Field>
              <Field>
                <Label htmlFor="phoneNumber">Phone Number</Label>
                <Input
                  id="phoneNumber"
                  name="phoneNumber"
                  value={phoneNumber}
                  onChange={onFormChange}
                  placeholder="(000) 000-0000"
                  autoComplete="tel"
                  required
                />
              </Field>
              <Field>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  name="email"
                  value={email}
                  onChange={onFormChange}
                  placeholder="you@company.com"
                  autoComplete="email"
                  required
                />
              </Field>
            </FieldGrid>

            <Field>
              <Label htmlFor="message">Message</Label>
              <TextArea
                id="message"
                name="message"
                placeholder="Tell us about your project…"
                onChange={onFormChange}
                value={message}
                required
              />
            </Field>

            <ButtonRow>
              <SubmitButton type="submit" disabled={!isValid}>
                Send
              </SubmitButton>
            </ButtonRow>
          </Form>
        </Panel>
      </Container>
    </Section>
  );
};
